/**
 * Carding Waste Studies — Sprint 0141.
 *
 * Route: /production/carding-waste-studies
 *
 * The monthly QC procedure for measuring per-card waste %.
 * Procedure (industry SOP):
 *   1. SETUP — pick card + count, record ambient temp + RH, sample duration
 *   2. ZERO — empty + tare every waste collection point
 *   3. RUN — start card with all collection points empty; 60 min default
 *   4. MEASURE — weigh sliver output + each waste component:
 *        flat strips, lickerin, cylinder, sliver clearer, framework fly
 *   5. COMPLETE — system computes waste% + realization%
 *   6. SIGN — QM counter-signs → service updates the matching
 *             CardProductionStandard.standard_waste_pct
 *
 * Each row is an immutable history record. Daily Carding section waste
 * prediction (Sprint 0140) uses the latest signed % per card.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Badge, Button, Card, Col, DatePicker, Divider, Drawer, Form,
  Input, InputNumber, Modal, Row, Select, Space, Statistic, Table, Tabs, Tag,
  Tooltip, Typography, message,
} from 'antd'
import {
  CheckCircleOutlined, ExperimentOutlined, FileExcelOutlined,
  FileTextOutlined, PlusOutlined, SignatureOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import {
  cardingApi, type CardWasteStudy,
} from '@/features/production/cardingApi'
import {
  SectionStudiesTab, SingleCardTab,
} from '@/pages/production/_CardingWasteSectionTab'
import { usePlantId } from '@/shared/hooks/usePlantId'
import apiClient from '@/shared/api/client'

const { Title, Text, Paragraph } = Typography

interface MachineLite { id: string; machine_code: string; machine_name: string }
interface CountLite { id: string; count_code: string; count_ne: number; description?: string }

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default',
  COMPLETED: 'blue',
  SIGNED: 'green',
}

export default function CardingWasteStudiesPage() {
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [createOpen, setCreateOpen] = useState(false)
  const [activeStudy, setActiveStudy] = useState<CardWasteStudy | null>(null)
  const [machineFilter, setMachineFilter] = useState<string | undefined>()
  const [createForm] = Form.useForm()

  const listQ = useQuery({
    enabled: !!plantId,
    queryKey: ['waste-studies', plantId, machineFilter],
    queryFn: () => cardingApi.listStudies({
      plant_id: plantId!,
      card_machine_id: machineFilter,
      limit: 200,
    }),
  })

  const machinesQ = useQuery({
    enabled: !!plantId,
    queryKey: ['machines-carding', plantId],
    queryFn: async () => {
      const r = await apiClient.get('/masters/machines', {
        params: { plant_id: plantId, is_active: true, size: 500 },
      })
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      // CLAUDE.md warns: MachineResponse uses legacy aliases
      //   `section` (not machine_type), `machine_id_str` (not machine_code),
      //   `name` (not machine_name). Defensive unwrap covers both.
      return raw
        .filter((m) => (m.machine_type ?? m.section ?? '').toUpperCase() === 'CARDING')
        .map((m): MachineLite => ({
          id: m.id,
          machine_code: m.machine_code ?? m.machine_id_str ?? '',
          machine_name: m.machine_name ?? m.name ?? '',
        }))
    },
  })

  const countsQ = useQuery({
    queryKey: ['counts-for-carding'],
    queryFn: async () => {
      const r = await apiClient.get('/masters/counts', {
        params: { is_active: true, size: 500 },
      })
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      // CountMasterResponse has no count_code — only count_ne + description.
      // Use description (e.g. "30s Combed") as label; fall back to "<ne>s".
      return raw.map((c): CountLite => ({
        id: c.id,
        count_code: c.count_code ?? c.description ?? `${c.count_ne ?? '?'}s`,
        count_ne: Number(c.count_ne ?? 0),
        description: c.description,
      }))
    },
  })

  const machineMap = useMemo(() => {
    const m = new Map<string, MachineLite>()
    for (const x of machinesQ.data ?? []) m.set(x.id, x)
    return m
  }, [machinesQ.data])

  const machineOptions = (machinesQ.data ?? []).map((m) => ({
    label: `${m.machine_code} — ${m.machine_name}`,
    value: m.id,
  }))
  // Count label: prefer description (e.g. "30s Combed Compact"), else "{ne}s".
  const countOptions = (countsQ.data ?? []).map((c) => {
    const ne = c.count_ne ? `${c.count_ne}s` : ''
    const desc = c.description && c.description !== c.count_code ? c.description : ''
    const label = [ne, desc].filter(Boolean).join(' — ') || c.count_code || c.id.slice(0, 6)
    return { label, value: c.id }
  })

  const createMut = useMutation({
    mutationFn: cardingApi.createStudy,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['waste-studies'] })
      setCreateOpen(false)
      createForm.resetFields()
      message.success('Study created — proceed to measurements')
      setActiveStudy(data)
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Failed to create study'),
  })

  // ─── Table ─────────────────────────────────────────────
  const rows = listQ.data ?? []
  const columns = [
    {
      title: 'Date',
      dataIndex: 'study_date',
      key: 'study_date',
      width: 100,
      render: (v: string) => dayjs(v).format('DD MMM YYYY'),
    },
    {
      title: 'Card',
      dataIndex: 'card_machine_id',
      key: 'card_machine_id',
      width: 100,
      render: (v: string) => machineMap.get(v)?.machine_code ?? v.slice(0, 6),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>,
    },
    {
      title: 'Waste %',
      dataIndex: 'waste_pct',
      key: 'waste_pct',
      width: 90,
      align: 'right' as const,
      render: (v: number | null) => {
        if (v === null) return '—'
        const color = v > 6 ? '#ff4d4f' : v > 5 ? '#faad14' : '#52c41a'
        return <Text strong style={{ color }}>{v.toFixed(2)}%</Text>
      },
    },
    {
      title: 'Realization %',
      dataIndex: 'realization_pct',
      key: 'realization_pct',
      width: 110,
      align: 'right' as const,
      render: (v: number | null) => v === null ? '—' : `${v.toFixed(2)}%`,
    },
    {
      title: 'Sample (kg)',
      key: 'sample',
      width: 140,
      render: (_: unknown, r: CardWasteStudy) =>
        r.sample_input_kg
          ? `${r.sample_input_kg.toFixed(1)} in → ${(r.sample_output_kg ?? 0).toFixed(1)} out`
          : '—',
    },
    {
      title: 'Duration',
      dataIndex: 'sample_duration_min',
      key: 'duration',
      width: 90,
      align: 'right' as const,
      render: (v: number) => `${v} min`,
    },
    {
      title: 'Ambient',
      key: 'ambient',
      width: 110,
      render: (_: unknown, r: CardWasteStudy) =>
        r.ambient_temp_c && r.ambient_rh_pct
          ? `${r.ambient_temp_c}°C / ${r.ambient_rh_pct}% RH`
          : '—',
    },
    {
      title: 'Signed',
      key: 'signed',
      width: 110,
      render: (_: unknown, r: CardWasteStudy) =>
        r.qm_signed_at
          ? <Tag color="green" icon={<SignatureOutlined />}>
              {dayjs(r.qm_signed_at).format('DD MMM')}
            </Tag>
          : <Tag>Pending</Tag>,
    },
    {
      title: '',
      key: 'action',
      fixed: 'right' as const,
      width: 80,
      render: (_: unknown, r: CardWasteStudy) =>
        <Button size="small" onClick={() => setActiveStudy(r)}>Open</Button>,
    },
  ]

  // Master-data preflight — Carding cards or count master missing?
  const noMachines = machinesQ.isSuccess && (machinesQ.data?.length ?? 0) === 0
  const noCounts = countsQ.isSuccess && (countsQ.data?.length ?? 0) === 0

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Carding Waste Studies</Title>
      <Text type="secondary">
        Section studies (all N cards in parallel, the monthly SOP) and ad-hoc
        single-card studies. Each signed study updates the per-card standard
        used for daily Carding section waste prediction.
      </Text>

      {/* Master-data preflight — visible if the lookups returned empty.
          Catches the most common gotcha: dropdowns are empty because
          the masters haven't been set up, not because the page is broken. */}
      {(noMachines || noCounts) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="Master data missing — set this up first"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {noMachines && (
                <li>
                  No CARDING machines found in this plant.{' '}
                  <a href="/masters/machines">Add machines →</a> with
                  <Text code>machine_type = CARDING</Text>.
                </li>
              )}
              {noCounts && (
                <li>
                  No counts in the Count Master.{' '}
                  <a href="/masters/counts">Add counts →</a> (e.g. 30s Combed,
                  40s Carded).
                </li>
              )}
            </ul>
          }
        />
      )}

      <Tabs
        defaultActiveKey="section"
        style={{ marginTop: 12 }}
        items={[
          {
            key: 'section',
            label: <span><ExperimentOutlined /> Section Studies (recommended)</span>,
            children: plantId
              ? <SectionStudiesTab plantId={plantId} machineMap={machineMap} machineOptions={machineOptions} countOptions={countOptions} />
              : null,
          },
          {
            key: 'single',
            label: <span><FileTextOutlined /> Single-card (ad-hoc)</span>,
            children: (
              <SingleCardTab
                rows={rows}
                columns={columns}
                loading={listQ.isLoading}
                machineOptions={machineOptions}
                machineFilter={machineFilter}
                setMachineFilter={setMachineFilter}
                onNew={() => {
                  createForm.resetFields()
                  createForm.setFieldsValue({
                    study_date: dayjs(),
                    sample_duration_min: 60,
                  })
                  setCreateOpen(true)
                }}
              />
            ),
          },
        ]}
      />

      {/* Create Study drawer */}
      <Drawer
        title="New Card Waste Study — Step 1: Setup"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        width={520}
        destroyOnClose
      >
        <Paragraph type="secondary">
          Set up the study. After saving, you'll record measurements in the
          next step. Card must be running ≥2 hrs since last doff and same
          count throughout the 60 min window.
        </Paragraph>
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(v) => {
            if (!plantId) return message.error('Plant not loaded')
            createMut.mutate({
              plant_id: plantId,
              card_machine_id: v.card_machine_id,
              count_id: v.count_id,
              study_date: (v.study_date as dayjs.Dayjs).format('YYYY-MM-DD'),
              ambient_temp_c: v.ambient_temp_c,
              ambient_rh_pct: v.ambient_rh_pct,
              sample_duration_min: v.sample_duration_min ?? 60,
              notes: v.notes,
            })
          }}
        >
          <Form.Item
            name="card_machine_id"
            label="Card"
            rules={[{ required: true, message: 'Pick a card' }]}
          >
            <Select
              showSearch
              placeholder="Pick card (e.g. CD-01)"
              options={machineOptions}
              filterOption={(input, option) =>
                (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="count_id" label="Count being run">
            <Select
              allowClear
              placeholder="(optional but recommended)"
              options={countOptions}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="study_date"
                label="Study date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="sample_duration_min"
                label="Sample duration (min)"
                tooltip="Industry default 60 min. Long enough for measurable waste, short enough for stable state."
              >
                <InputNumber min={15} max={240} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">Ambient conditions</Divider>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="ambient_temp_c"
                label="Temp (°C)"
                tooltip="Card waste shifts with humidity — record both for trend interpretation."
              >
                <InputNumber min={0} max={60} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ambient_rh_pct" label="Relative humidity (%)">
                <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes (optional)">
            <Input.TextArea rows={2} placeholder="e.g. Flats replaced last week" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={createMut.isPending} block>
              Create study → proceed to measurements
            </Button>
          </Form.Item>
        </Form>
      </Drawer>

      {/* Measurement / Review / Sign drawer */}
      {activeStudy && (
        <StudyDrawer
          study={activeStudy}
          machineLabel={machineMap.get(activeStudy.card_machine_id)?.machine_code ?? '—'}
          onClose={() => setActiveStudy(null)}
          onChanged={(s) => {
            setActiveStudy(s)
            qc.invalidateQueries({ queryKey: ['waste-studies'] })
            qc.invalidateQueries({ queryKey: ['carding-settings'] })
          }}
        />
      )}
    </div>
  )
}

// ─── Study detail / measurement drawer ─────────────────────────

function StudyDrawer({
  study, machineLabel, onClose, onChanged,
}: {
  study: CardWasteStudy
  machineLabel: string
  onClose: () => void
  onChanged: (s: CardWasteStudy) => void
}) {
  const [form] = Form.useForm()
  const editable = study.status === 'DRAFT'
  const completed = study.status === 'COMPLETED'
  const signed = study.status === 'SIGNED'

  const patchMut = useMutation({
    mutationFn: (body: any) => cardingApi.patchStudy(study.id, body),
    onSuccess: (s) => { message.success('Saved'); onChanged(s) },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })
  const completeMut = useMutation({
    mutationFn: () => cardingApi.completeStudy(study.id),
    onSuccess: (s) => { message.success('Computed — review then sign'); onChanged(s) },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Complete failed'),
  })
  const signMut = useMutation({
    mutationFn: () => cardingApi.signStudy(study.id),
    onSuccess: (s) => {
      message.success('Signed — standard updated for daily prediction')
      onChanged(s)
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Sign failed'),
  })

  // Σ components for the running total preview
  const componentSum = useMemo(() => {
    const v = form.getFieldsValue()
    return (
      (v.flat_strips_kg ?? study.flat_strips_kg ?? 0) +
      (v.lickerin_kg ?? study.lickerin_kg ?? 0) +
      (v.cylinder_kg ?? study.cylinder_kg ?? 0) +
      (v.sliver_clearer_kg ?? study.sliver_clearer_kg ?? 0) +
      (v.framework_fly_kg ?? study.framework_fly_kg ?? 0)
    )
  }, [study, form])

  const handleSave = () => {
    const v = form.getFieldsValue()
    patchMut.mutate({
      sample_output_kg: v.sample_output_kg ?? undefined,
      flat_strips_kg: v.flat_strips_kg ?? undefined,
      lickerin_kg: v.lickerin_kg ?? undefined,
      cylinder_kg: v.cylinder_kg ?? undefined,
      sliver_clearer_kg: v.sliver_clearer_kg ?? undefined,
      framework_fly_kg: v.framework_fly_kg ?? undefined,
      notes: v.notes ?? undefined,
    })
  }

  return (
    <Drawer
      open
      onClose={onClose}
      width={640}
      title={
        <Space>
          <Text strong>{machineLabel}</Text>
          <Tag color={STATUS_COLOR[study.status]}>{study.status}</Tag>
          <Text type="secondary">{dayjs(study.study_date).format('DD MMM YYYY')}</Text>
        </Space>
      }
      destroyOnClose
    >
      {/* Conditions */}
      <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
        <Row gutter={12}>
          <Col span={8}>
            <Statistic title="Duration" value={study.sample_duration_min} suffix="min" />
          </Col>
          <Col span={8}>
            <Statistic
              title="Temp"
              value={study.ambient_temp_c ?? '—'}
              suffix={study.ambient_temp_c ? '°C' : ''}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="RH"
              value={study.ambient_rh_pct ?? '—'}
              suffix={study.ambient_rh_pct ? '%' : ''}
            />
          </Col>
        </Row>
      </Card>

      {/* Computed result (after complete) */}
      {(completed || signed) && (
        <Card
          size="small"
          style={{
            marginBottom: 12,
            background: signed ? '#f6ffed' : '#e6f7ff',
            borderColor: signed ? '#b7eb8f' : '#91d5ff',
          }}
        >
          <Row gutter={12}>
            <Col span={6}>
              <Statistic
                title="Total waste"
                value={study.sample_waste_kg ?? '—'}
                suffix={study.sample_waste_kg ? 'kg' : ''}
                precision={3}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Output (sliver)"
                value={study.sample_output_kg ?? '—'}
                suffix={study.sample_output_kg ? 'kg' : ''}
                precision={3}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Waste %"
                value={study.waste_pct ?? '—'}
                suffix={study.waste_pct ? '%' : ''}
                precision={2}
                valueStyle={{
                  color:
                    (study.waste_pct ?? 0) > 6 ? '#ff4d4f'
                    : (study.waste_pct ?? 0) > 5 ? '#faad14'
                    : '#52c41a',
                }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Realization %"
                value={study.realization_pct ?? '—'}
                suffix={study.realization_pct ? '%' : ''}
                precision={2}
              />
            </Col>
          </Row>
          {signed && study.qm_signed_at && (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              style={{ marginTop: 12 }}
              message={
                <span>
                  Signed at {dayjs(study.qm_signed_at).format('DD MMM YYYY HH:mm')} — daily prediction
                  standard now uses <Text strong>{study.waste_pct?.toFixed(2)}%</Text>.
                </span>
              }
            />
          )}
        </Card>
      )}

      {/* Measurements form (editable in DRAFT only) */}
      <Form
        form={form}
        layout="vertical"
        disabled={!editable}
        initialValues={{
          sample_output_kg: study.sample_output_kg ?? undefined,
          flat_strips_kg: study.flat_strips_kg ?? undefined,
          lickerin_kg: study.lickerin_kg ?? undefined,
          cylinder_kg: study.cylinder_kg ?? undefined,
          sliver_clearer_kg: study.sliver_clearer_kg ?? undefined,
          framework_fly_kg: study.framework_fly_kg ?? undefined,
          notes: study.notes ?? undefined,
        }}
      >
        <Divider orientation="left">Step 4: Measurements</Divider>
        <Form.Item
          name="sample_output_kg"
          label="Sliver output kg (during the run)"
          tooltip="Weigh cans produced × calibrated can weight, OR meter counter × ktex / 1000."
        >
          <InputNumber
            min={0}
            step={0.1}
            style={{ width: '100%' }}
            placeholder="e.g. 48.5"
          />
        </Form.Item>

        <Divider orientation="left">Waste components</Divider>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          Weigh each separately. Industry typical share shown for sanity check.
          If a component is way out of range, double-check collection.
        </Paragraph>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="flat_strips_kg"
              label="Flat strips kg (40-50% of waste)"
              tooltip="Rotating flats — biggest single waste source. Short fibre + neps."
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lickerin_kg"
              label="Licker-in kg (25-30%)"
              tooltip="Trash, sand, leaves, immature fibre dropped by licker-in undercasing + mote knife."
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="cylinder_kg"
              label="Cylinder kg (10-15%)"
              tooltip="Cylinder undercasing — fine fly + short fibre."
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="sliver_clearer_kg"
              label="Sliver clearer kg (5-10%)"
              tooltip="Naps, foreign matter caught by the web clearer."
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="framework_fly_kg"
          label="Framework fly kg (~5%)"
          tooltip="Loose fibre collected from card framework + coiler droppings."
        >
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>

        {editable && componentSum > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <span>
                <Text strong>Σ components:</Text> {componentSum.toFixed(3)} kg
                {' '}— that will be your total waste once you Complete.
              </span>
            }
          />
        )}

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} disabled={signed} />
        </Form.Item>
      </Form>

      {/* Actions */}
      <Divider />
      <Space>
        {editable && (
          <>
            <Button type="default" onClick={handleSave} loading={patchMut.isPending}>
              Save draft
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Complete this study?',
                  content: 'Saves measurements and computes waste% + realization%. After this, measurements cannot be edited.',
                  onOk: async () => {
                    // Save current form first, then complete
                    await patchMut.mutateAsync({
                      sample_output_kg: form.getFieldValue('sample_output_kg'),
                      flat_strips_kg: form.getFieldValue('flat_strips_kg'),
                      lickerin_kg: form.getFieldValue('lickerin_kg'),
                      cylinder_kg: form.getFieldValue('cylinder_kg'),
                      sliver_clearer_kg: form.getFieldValue('sliver_clearer_kg'),
                      framework_fly_kg: form.getFieldValue('framework_fly_kg'),
                      notes: form.getFieldValue('notes'),
                    })
                    completeMut.mutate()
                  },
                })
              }}
              loading={completeMut.isPending}
            >
              Complete
            </Button>
          </>
        )}
        {completed && (
          <Button
            type="primary"
            icon={<SignatureOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Sign this study?',
                content: `QM sign-off updates the standard waste % for this card to ${study.waste_pct?.toFixed(2)}%. Daily Carding section waste prediction will use this number going forward. Signed studies are immutable.`,
                onOk: () => signMut.mutate(),
              })
            }}
            loading={signMut.isPending}
          >
            QM Sign-off
          </Button>
        )}
        {signed && (
          <Tooltip title="Signed — immutable">
            <Button icon={<FileTextOutlined />} disabled>Audit only</Button>
          </Tooltip>
        )}
      </Space>
    </Drawer>
  )
}
